import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState,
} from "react";
import { getJSON, setJSON, removeItem, onChange } from "./safeStorage";

// Persisted under this key. Includes the masked phone and the most-recent
// points balance so any page (header, cart drawer, footer) can render the
// shopper's status without a fresh API round-trip.
const LS_KEY = "zen_loyalty_v1";

const LoyaltyContext = createContext({
    open: false,
    points: null,
    phone: null,
    openModal: () => {},
    closeModal: () => {},
    setVerifiedBalance: () => {},
    clearLoyalty: () => {},
});

export function LoyaltyProvider({ children }) {
    const [open, setOpen] = useState(false);
    const [points, setPoints] = useState(null);
    const [phone, setPhone] = useState(null);

    // Hydrate from localStorage once on mount.
    useEffect(() => {
        const parsed = getJSON(LS_KEY, null);
        if (parsed && typeof parsed === "object") {
            Promise.resolve().then(() => {
                if (typeof parsed.points === "number") setPoints(parsed.points);
                if (typeof parsed.phone === "string") setPhone(parsed.phone);
            });
        }
    }, []);

    // Cross-tab sync — if the user verifies in another tab, mirror state
    // here (and the reverse: if a logout removes the key, clear local state).
    useEffect(() => {
        return onChange(LS_KEY, (newVal) => {
            Promise.resolve().then(() => {
                if (!newVal) {
                    setPoints(null);
                    setPhone(null);
                    return;
                }
                try {
                    const parsed = JSON.parse(newVal);
                    if (parsed && typeof parsed === "object") {
                        if (typeof parsed.points === "number") setPoints(parsed.points);
                        if (typeof parsed.phone === "string") setPhone(parsed.phone);
                    }
                } catch (e) {
                    /* malformed cross-tab payload — ignore */
                    if (typeof console !== "undefined")
                        console.debug("[zen-loyalty] cross-tab parse failed:", e?.name);
                }
            });
        });
    }, []);

    const setVerifiedBalance = useCallback(({ points: p, phone: ph }) => {
        const safePoints = typeof p === "number" ? p : 0;
        setPoints(safePoints);
        if (ph) setPhone(ph);
        setJSON(LS_KEY, { points: safePoints, phone: ph || null });
    }, []);

    const clearLoyalty = useCallback(() => {
        setPoints(null);
        setPhone(null);
        removeItem(LS_KEY);
    }, []);

    const value = useMemo(
        () => ({
            open,
            points,
            phone,
            openModal: () => setOpen(true),
            closeModal: () => setOpen(false),
            setVerifiedBalance,
            clearLoyalty,
        }),
        [open, points, phone, setVerifiedBalance, clearLoyalty]
    );

    return (
        <LoyaltyContext.Provider value={value}>
            {children}
        </LoyaltyContext.Provider>
    );
}

export function useLoyalty() {
    return useContext(LoyaltyContext);
}

// Mask a 10-digit phone like 5169961746 -> (516) ••• -1746
export function maskPhone(phone) {
    if (!phone) return "";
    const d = String(phone).replace(/\D/g, "");
    if (d.length < 10) return phone;
    return `(${d.slice(0, 3)}) ••• -${d.slice(-4)}`;
}
